import { useEffect } from 'react';

/**
 * Custom hook to handle global keydown shortcuts.
 * * @param {string} targetKey - The key to listen for (e.g., 'F8', 'F5')
 * @param {Function} callback - The function to run when conditions match
 * @param {Object} options - Configuration options
 * @param {boolean} options.altKey - Whether the Alt key is required (default: false)
 * @param {boolean} options.ctrlKey - Whether the Ctrl key is required (default: false)
 * @param {boolean} options.shiftKey - Whether the Shift key is required (default: false)
 * @param {boolean} options.condition - An optional condition (e.g., voucherType === 'Sale')
 * @param {Array} dependencies - Dependency array to re-bind the listener when values change
 */
export const useKeyPress = (
    targetKey, 
    callback, 
    { altKey = false, ctrlKey = false, shiftKey = false, condition = true } = {}, 
    dependencies = []
) => {
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Strict match for modifier keys: they must exactly match your configuration
            const altMatch = event.altKey === altKey;
            const ctrlMatch = event.ctrlKey === ctrlKey;
            const shiftMatch = event.shiftKey === shiftKey;
            
            // Check if the primary key, all modifiers, and your custom condition match
            if (event.key === targetKey && altMatch && ctrlMatch && shiftMatch && condition) {
                event.preventDefault();
                callback();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [targetKey, callback, altKey, ctrlKey, shiftKey, condition, ...dependencies]);
};